import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useVendors, useVendorEvents, useCreateVendor, useUpdateVendor, useBulkImportVendors, useCreateVendorEvent, useEvent, useCanAddMenuItems } from '@/lib/api/queries'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, Plus, Store, MapPin, ChevronDown, ChevronUp, Pencil, Info, X, Upload, Image, CheckCircle, AlertCircle, Download, FileSpreadsheet, FileText, KeyRound, Loader2, Package } from 'lucide-react'
import { useState, useMemo, useRef, useEffect } from 'react'
import { usePermission, useAllPermissions } from '@/lib/permissions'
import { useAuth } from '@/lib/auth'
import { AccessDenied } from '@/components/permission-guard'
import type { Vendor, VendorEvent } from '@/lib/api/types'
import { EditLocationModal } from './vendors.$vendorId.locations.$locationId'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api/client'
import { ExportLoginPdfModal } from '@/components/export-login-pdf-modal'
import { EventCategoriesPanel } from '@/components/event-categories-panel'
import { CreateMenuItemModal } from './menu'

export const Route = createFileRoute('/_authenticated/events/$eventId/vendors')({
  component: VendorsLayout,
  // Which tab is open lives in the URL so a view can be linked and survives a
  // refresh. 'vendors' is the default and stays out of the URL.
  validateSearch: (search: Record<string, unknown>): { tab?: 'locations' | 'categories' } => {
    return search.tab === 'locations' || search.tab === 'categories' ? { tab: search.tab } : {}
  },
})

// Layout wrapper that renders either the vendors list or child routes
function VendorsLayout() {
  const matches = useRouterState({ select: (s) => s.matches })
  
  // Check if this is the deepest match (no child routes active)
  // If the last match is this vendors route, show the list
  // Otherwise, a child route is active, render Outlet
  const lastMatch = matches[matches.length - 1]
  const isVendorsListPage = lastMatch?.routeId === '/_authenticated/events/$eventId/vendors'
  
  if (isVendorsListPage) {
    return <VendorsPage />
  }
  
  return <Outlet />
}

type SortField = 'name' | 'locations' | 'items_sold' | 'revenue' | 'is_approved' | 'booth' | 'vendor' | 'completed_orders'
type SortDirection = 'asc' | 'desc'
type TabType = 'vendors' | 'locations' | 'categories'

function VendorsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const { token } = useAuth()

  // Permission checks
  const canViewVendors = usePermission('can_view_vendors')
  const canCreateVendors = usePermission('can_create_vendors')
  const canEditVendors = usePermission('can_edit_vendors')
  const canExportSales = useAllPermissions(['can_export_reports', 'can_view_reporting'])
  const canExportVendorLogins = usePermission('can_export_vendor_logins')
  
  const navigate = useNavigate()
  const { tab } = Route.useSearch()
  const activeTab: TabType = tab ?? 'vendors'
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [showNewVendorModal, setShowNewVendorModal] = useState(false)
  const [showNewLocationModal, setShowNewLocationModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<VendorEvent | null>(null)
  const [showNewMenuItemModal, setShowNewMenuItemModal] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [showExportLoginPdfModal, setShowExportLoginPdfModal] = useState(false)
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false)
  const [showNewDropdown, setShowNewDropdown] = useState(false)
  const [exportingFormat, setExportingFormat] = useState<string | null>(null)
  const optionsDropdownRef = useRef<HTMLDivElement>(null)
  const newDropdownRef = useRef<HTMLDivElement>(null)

  // Replace rather than push: the URL stays shareable without every tab click
  // becoming a Back step.
  const selectTab = (next: TabType) => {
    if (next === 'vendors') {
      setSortField('name')
      setSortDirection('asc')
    } else if (next === 'locations') {
      setSortField('booth')
      setSortDirection('asc')
    }
    navigate({
      to: '/events/$eventId/vendors',
      params: { eventId },
      search: next === 'vendors' ? {} : { tab: next },
      replace: true,
    })
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsDropdownRef.current && !optionsDropdownRef.current.contains(event.target as Node)) {
        setShowOptionsDropdown(false)
      }
      if (newDropdownRef.current && !newDropdownRef.current.contains(event.target as Node)) {
        setShowNewDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const hasOptionsDropdown = canExportSales || canExportVendorLogins || canCreateVendors

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    if (!token) return
    setExportingFormat(format)
    try {
      await apiClient.exportSales(token, eventIdNum, format)
      setShowOptionsDropdown(false)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExportingFormat(null)
    }
  }
  
  // Fetch event data (needed for menu item creation)
  const { data: eventData } = useEvent(eventIdNum)
  const canAddMenuItems = useCanAddMenuItems(eventIdNum)

  // Fetch vendors with aggregates - hooks must be before any early returns
  const { data: vendorsData, isLoading: vendorsLoading } = useVendors({
    event: eventIdNum,
    page_size: 100,
  })
  
  // Fetch vendor events (locations) with aggregates
  const { data: locationsData, isLoading: locationsLoading } = useVendorEvents(eventIdNum, {
    limit: 100,
  })

  const vendors = vendorsData?.results || []
  const locations = locationsData?.results || []

  // Filter and sort vendors
  const filteredVendors = useMemo(() => {
    let result = vendors.filter(v => 
      v.name.toLowerCase().includes(search.toLowerCase())
    )
    
    result.sort((a, b) => {
      let aVal: string | number | boolean = ''
      let bVal: string | number | boolean = ''
      
      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase()
          bVal = b.name.toLowerCase()
          break
        case 'locations':
          aVal = a.vendorevent_set?.length || 0
          bVal = b.vendorevent_set?.length || 0
          break
        case 'items_sold':
          aVal = a.items_sold || a.aggregates?.items_sold || 0
          bVal = b.items_sold || b.aggregates?.items_sold || 0
          break
        case 'revenue':
          aVal = a.revenue || a.aggregates?.revenue || 0
          bVal = b.revenue || b.aggregates?.revenue || 0
          break
        case 'is_approved':
          aVal = a.is_approved ? 1 : 0
          bVal = b.is_approved ? 1 : 0
          break
      }
      
      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })
    
    return result
  }, [vendors, search, sortField, sortDirection])

  // Filter and sort locations
  const filteredLocations = useMemo(() => {
    let result = locations.filter(l => {
      const vendorName = typeof l.vendor === 'object' ? l.vendor.name : ''
      return (
        l.booth?.toLowerCase().includes(search.toLowerCase()) ||
        vendorName.toLowerCase().includes(search.toLowerCase())
      )
    })
    
    result.sort((a, b) => {
      let aVal: string | number | boolean = ''
      let bVal: string | number | boolean = ''
      
      const aVendorName = typeof a.vendor === 'object' ? a.vendor.name : ''
      const bVendorName = typeof b.vendor === 'object' ? b.vendor.name : ''
      
      switch (sortField) {
        case 'booth':
          aVal = (a.booth || '').toLowerCase()
          bVal = (b.booth || '').toLowerCase()
          break
        case 'vendor':
          aVal = aVendorName.toLowerCase()
          bVal = bVendorName.toLowerCase()
          break
        case 'completed_orders':
          aVal = a.completed_orders || a.aggregates?.completed_orders || 0
          bVal = b.completed_orders || b.aggregates?.completed_orders || 0
          break
        case 'revenue':
          aVal = a.revenue || a.tokens_spent || a.total || a.aggregates?.revenue || 0
          bVal = b.revenue || b.tokens_spent || b.total || b.aggregates?.revenue || 0
          break
        case 'is_approved':
          aVal = a.is_approved ? 1 : 0
          bVal = b.is_approved ? 1 : 0
          break
      }
      
      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })
    
    return result
  }, [locations, search, sortField, sortDirection])

  // Permission guard - now safe after all hooks
  if (!canViewVendors) {
    return <AccessDenied message="You don't have permission to view vendors." />
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-0 group-hover:opacity-50" />
    return sortDirection === 'asc' 
      ? <ChevronDown className="w-4 h-4" />
      : <ChevronUp className="w-4 h-4" />
  }

  const getVendorItemsSold = (vendor: Vendor) => {
    return vendor.items_sold || vendor.aggregates?.items_sold || 0
  }

  const getVendorRevenue = (vendor: Vendor) => {
    // Prefer top-level tokens_spent (returned by API with aggregates=1)
    if (vendor.tokens_spent) return vendor.tokens_spent
    if (vendor.aggregates?.tokens_spent) return vendor.aggregates.tokens_spent
    if (vendor.aggregates?.revenue) return vendor.aggregates.revenue
    if (vendor.revenue) return vendor.revenue
    // Sum tokens_spent/total from nested vendor events as fallback
    if (vendor.vendorevent_set?.length) {
      return vendor.vendorevent_set.reduce((sum, ve) => sum + (ve.tokens_spent || ve.total || ve.revenue || ve.aggregates?.tokens_spent || 0), 0)
    }
    return 0
  }

  const getLocationRevenue = (location: VendorEvent) => {
    return location.tokens_spent || location.total || location.revenue || location.aggregates?.tokens_spent || location.aggregates?.revenue || 0
  }

  const getLocationCompletedOrders = (location: VendorEvent) => {
    return location.completed_orders || location.aggregates?.completed_orders || 0
  }

  const isLoading = activeTab === 'vendors' ? vendorsLoading : locationsLoading

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="flex flex-row justify-between items-center mb-3 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          Vendors
        </h1>
        <div className="flex items-center gap-2">
          {hasOptionsDropdown && (
            <div className="relative" ref={optionsDropdownRef}>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
              >
                Options
                <ChevronDown className={cn('w-4 h-4 transition-transform', showOptionsDropdown && 'rotate-180')} />
              </Button>
              {showOptionsDropdown && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 min-w-[210px]">
                  {(canExportSales || canExportVendorLogins) && (
                    <>
                      <div className="px-3 py-1.5 text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                        Export
                      </div>
                      {canExportSales && (
                        <>
                          <button
                            onClick={() => handleExport('xlsx')}
                            disabled={exportingFormat !== null}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
                          >
                            {exportingFormat === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <FileSpreadsheet className="w-4 h-4 text-green-600 dark:text-green-500" />}
                            Export as XLSX
                          </button>
                          <button
                            onClick={() => handleExport('pdf')}
                            disabled={exportingFormat !== null}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
                          >
                            {exportingFormat === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <FileText className="w-4 h-4 text-red-600 dark:text-red-500" />}
                            Export as PDF
                          </button>
                        </>
                      )}
                      {canExportVendorLogins && (
                        <button
                          onClick={() => { setShowExportLoginPdfModal(true); setShowOptionsDropdown(false) }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <KeyRound className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                          Export Login PDF
                        </button>
                      )}
                    </>
                  )}
                  {canCreateVendors && (
                    <>
                      {(canExportSales || canExportVendorLogins) && (
                        <div className="my-1 border-t border-neutral-200 dark:border-neutral-800" />
                      )}
                      <div className="px-3 py-1.5 text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                        Import
                      </div>
                      <button
                        onClick={() => { setShowBulkImportModal(true); setShowOptionsDropdown(false) }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                        Bulk Import
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {canCreateVendors && (
            <div className="relative" ref={newDropdownRef}>
              <Button className="gap-2" onClick={() => setShowNewDropdown(!showNewDropdown)}>
                <Plus className="w-4 h-4" />
                New
                <ChevronDown className={cn('w-4 h-4 transition-transform', showNewDropdown && 'rotate-180')} />
              </Button>
              {showNewDropdown && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 min-w-[180px]">
                  <button
                    onClick={() => { setShowNewVendorModal(true); setShowNewDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Store className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    Vendor
                  </button>
                  <button
                    onClick={() => { setShowNewLocationModal(true); setShowNewDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    Location
                  </button>
                  {canAddMenuItems && (
                    <button
                      onClick={() => { setShowNewMenuItemModal(true); setShowNewDropdown(false) }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <Package className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                      Menu Item
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex space-x-6">
          <button
            onClick={() => selectTab('vendors')}
            className={cn(
              'px-1 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'vendors'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            All Vendors
          </button>
          <button
            onClick={() => selectTab('locations')}
            className={cn(
              'px-1 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'locations'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            All Locations
          </button>
          <button
            onClick={() => selectTab('categories')}
            className={cn(
              'px-1 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'categories'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            Categories
          </button>
        </div>
      </div>

      {/* Search (not meaningful on the categories tab) */}
      {activeTab !== 'categories' && (
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            type="search"
            placeholder={activeTab === 'vendors' 
              ? "Search by Vendor, Owner, Location, Phone or Email" 
              : "Search by Vendor, Location, Booth or Description"
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-950">
              <tr>
                {[1, 2, 3, 4, 5].map(i => (
                  <th key={i} className="px-6 py-3">
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20 animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-32" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-8" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-16" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-20" /></td>
                  <td className="px-6 py-4"><div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-16" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === 'vendors' ? (
        /* Vendors Table */
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-950">
                <tr>
                  <th 
                    onClick={() => handleSort('name')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Vendor <SortIcon field="name" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('locations')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Locations <SortIcon field="locations" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('items_sold')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Items Sold <SortIcon field="items_sold" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('revenue')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Token Revenue <SortIcon field="revenue" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('is_approved')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Is Approved <SortIcon field="is_approved" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                {filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Store className="w-10 h-10 mx-auto text-neutral-400 dark:text-neutral-600 mb-2" />
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No vendors found.</p>
                      {search && <p className="text-xs text-neutral-400 mt-1">Try a different search term.</p>}
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-950">
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to="/events/$eventId/vendors/$vendorId"
                            params={{ eventId, vendorId: String(vendor.id) }}
                            className="text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 hover:underline font-medium"
                          >
                            {vendor.name}
                          </Link>
                          <span className="relative group inline-flex">
                            <Info className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-600 cursor-help" />
                          </span>
                          <Link
                            to="/events/$eventId/vendors/$vendorId"
                            params={{ eventId, vendorId: String(vendor.id) }}
                            hash="locations"
                            className="text-xs text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 hover:underline"
                          >
                            View Locations
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                        {vendor.vendorevent_set?.length || 1}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                        {getVendorItemsSold(vendor).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                        {getVendorRevenue(vendor).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        <span className={cn(
                          'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                          vendor.is_approved
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        )}>
                          {vendor.is_approved ? 'Approved' : 'Not Approved'}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                        {canEditVendors && (
                          <button 
                            className="text-neutral-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors"
                            title="Edit vendor"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingVendor(vendor)
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredVendors.length > 0 && (
            <div className="bg-white dark:bg-[#131313] px-6 py-3 border-t border-neutral-200 dark:border-neutral-800">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Showing <span className="font-medium">{filteredVendors.length}</span> of <span className="font-medium">{vendors.length}</span> vendors
              </p>
            </div>
          )}
        </div>
      ) : activeTab === 'locations' ? (
        /* Locations Table */
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-50 dark:bg-neutral-950">
                <tr>
                  <th 
                    onClick={() => handleSort('booth')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Location/Booth <SortIcon field="booth" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('vendor')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Vendor <SortIcon field="vendor" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('completed_orders')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Completed Orders <SortIcon field="completed_orders" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('revenue')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Token Revenue <SortIcon field="revenue" />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('is_approved')}
                    className="group px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <div className="flex items-center gap-1">
                      Is Approved <SortIcon field="is_approved" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                {filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <MapPin className="w-10 h-10 mx-auto text-neutral-400 dark:text-neutral-600 mb-2" />
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No vendor locations found.</p>
                      {search && <p className="text-xs text-neutral-400 mt-1">Try a different search term.</p>}
                    </td>
                  </tr>
                ) : (
                  filteredLocations.map((location) => {
                    const vendorObj = typeof location.vendor === 'object' ? location.vendor : null
                    const vendorName = vendorObj?.name || 'Unknown Vendor'
                    const vendorIdStr = vendorObj?.id ? String(vendorObj.id) : ''
                    return (
                      <tr key={location.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-950">
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          {vendorIdStr ? (
                            <Link
                              to="/events/$eventId/vendors/$vendorId/locations/$locationId"
                              params={{ eventId, vendorId: vendorIdStr, locationId: String(location.id) }}
                              className="text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 hover:underline font-medium"
                            >
                              {location.booth || 'N/A'}
                            </Link>
                          ) : (
                            <span className="text-neutral-500">{location.booth || 'N/A'}</span>
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          <div className="flex items-center gap-1.5">
                            {vendorIdStr ? (
                              <Link
                                to="/events/$eventId/vendors/$vendorId"
                                params={{ eventId, vendorId: vendorIdStr }}
                                className="text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400 hover:underline font-medium"
                              >
                                {vendorName}
                              </Link>
                            ) : (
                              <span className="text-neutral-900 dark:text-neutral-100">{vendorName}</span>
                            )}
                            <span className="relative group inline-flex">
                              <Info className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-600 cursor-help" />
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {getLocationCompletedOrders(location).toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                          {getLocationRevenue(location).toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <span className={cn(
                            'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                            location.is_approved
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          )}>
                            {location.is_approved ? 'Approved' : 'Not Approved'}
                          </span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                          {canEditVendors && vendorIdStr && (
                            <button
                              type="button"
                              onClick={() => setEditingLocation(location)}
                              className="text-neutral-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors"
                              title="Edit location"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          {filteredLocations.length > 0 && (
            <div className="bg-white dark:bg-[#131313] px-6 py-3 border-t border-neutral-200 dark:border-neutral-800">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Showing <span className="font-medium">{filteredLocations.length}</span> of <span className="font-medium">{locations.length}</span> locations
              </p>
            </div>
          )}
        </div>
      ) : (
        <EventCategoriesPanel eventId={eventIdNum} locations={locations} canEdit={canEditVendors} />
      )}

      {/* New Vendor Modal */}
      {showNewVendorModal && (
        <NewVendorModal 
          eventId={eventIdNum} 
          onClose={() => setShowNewVendorModal(false)} 
        />
      )}

      {/* Edit Vendor Modal */}
      {editingVendor && (
        <EditVendorModal 
          vendor={editingVendor}
          onClose={() => setEditingVendor(null)} 
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportModal
          eventId={eventIdNum}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}

      {/* Export Login PDF Modal */}
      {canExportVendorLogins && showExportLoginPdfModal && (
        <ExportLoginPdfModal
          eventId={eventIdNum}
          vendors={vendors}
          vendorEvents={locations}
          onClose={() => setShowExportLoginPdfModal(false)}
        />
      )}

      {/* New Location Modal */}
      {showNewLocationModal && (
        <NewLocationModal
          eventId={eventIdNum}
          vendors={vendors}
          onClose={() => setShowNewLocationModal(false)}
        />
      )}

      {/* Edit Location Modal — quick edit; the full location page is via the booth-name link */}
      {editingLocation && (
        <EditLocationModal
          location={editingLocation}
          eventId={eventIdNum}
          onClose={() => setEditingLocation(null)}
        />
      )}

      {/* New Menu Item Modal */}
      {showNewMenuItemModal && eventData && (
        <CreateMenuItemModal
          eventId={eventIdNum}
          tokenValue={eventData.token_value || 1}
          eventType={eventData.event_type || 'token'}
          onClose={() => setShowNewMenuItemModal(false)}
        />
      )}
    </div>
  )
}

// New Vendor Modal Component
function NewVendorModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [website, setWebsite] = useState('')
  const [phone, setPhone] = useState('')
  const [isApproved, setIsApproved] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null)

  const logoInputRef = useRef<HTMLInputElement>(null)
  const backgroundInputRef = useRef<HTMLInputElement>(null)

  const createVendor = useCreateVendor()

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setBackgroundFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setBackgroundPreview(e.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLogoFile(null)
    setLogoPreview(null)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleRemoveBackground = (e: React.MouseEvent) => {
    e.stopPropagation()
    setBackgroundFile(null)
    setBackgroundPreview(null)
    if (backgroundInputRef.current) backgroundInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (!name.trim()) {
      setError('Vendor name is required')
      return
    }
    
    try {
      await createVendor.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        website: website.trim() || undefined,
        phone: phone.trim() || undefined,
        event: eventId,
        is_approved: isApproved,
        logo: logoFile || undefined,
        background: backgroundFile || undefined,
      })
      onClose()
    } catch (err) {
      console.error('Failed to create vendor:', err)
      setError(err instanceof Error ? err.message : 'Failed to create vendor')
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Store className="w-5 h-5 text-blue-600" />
              New Vendor
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200 text-sm">
                {error}
              </div>
            )}
            
            {/* Vendor Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                <Store className="w-4 h-4" />
                Vendor Details
              </h3>
              
              {/* Vendor Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter vendor name"
                  className="w-full"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Vendor description"
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              
              {/* Contact Info Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Website URL
                  </label>
                  <Input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Approved toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Approved
                </label>
                <button
                  type="button"
                  onClick={() => setIsApproved(!isApproved)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isApproved ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isApproved ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Branding Section */}
            <div className="space-y-4 border-t border-neutral-200 dark:border-neutral-800 pt-6">
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Vendor Branding
              </h3>
              
              {/* Upload Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Upload Logo */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Logo
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-800"
                    >
                      <div className="flex flex-col items-center">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain mb-2" />
                        ) : (
                          <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                        )}
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          {logoPreview ? 'Change Logo' : 'Upload'}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">PNG, JPG, SVG, WebP up to 2MB</span>
                      </div>
                    </button>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        aria-label="Remove logo"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Upload Background */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Background
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => backgroundInputRef.current?.click()}
                      className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-800"
                    >
                      <div className="flex flex-col items-center">
                        {backgroundPreview ? (
                          <img src={backgroundPreview} alt="Background preview" className="w-16 h-10 object-cover mb-2 rounded" />
                        ) : (
                          <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                        )}
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          {backgroundPreview ? 'Change Background' : 'Upload'}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">JPG, PNG, WebP up to 5MB</span>
                      </div>
                    </button>
                    {backgroundPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveBackground}
                        aria-label="Remove background"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                  />
                </div>
              </div>
              
              {/* Preview Section */}
              {(logoPreview || backgroundPreview) && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Preview
                  </label>
                  <div 
                    className="relative w-full h-40 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700"
                    style={backgroundPreview ? { 
                      backgroundImage: `url(${backgroundPreview})`, 
                      backgroundSize: 'cover', 
                      backgroundPosition: 'center' 
                    } : {}}
                  >
                    {backgroundPreview && (
                      <div className="absolute inset-0 bg-black/20" />
                    )}
                    {logoPreview && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img src={logoPreview} alt="Logo preview" className="max-w-24 max-h-24 object-contain drop-shadow-lg" />
                      </div>
                    )}
                    {!logoPreview && !backgroundPreview && (
                      <div className="absolute inset-0 flex items-center justify-center text-neutral-400">
                        <p className="text-sm">Upload images to see preview</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">This preview shows how your logo will appear on the background</p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createVendor.isPending}>
                Create Vendor
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Edit Vendor Modal Component
function EditVendorModal({ vendor, onClose }: { vendor: Vendor; onClose: () => void }) {
  const [name, setName] = useState(vendor.name || '')
  const [description, setDescription] = useState(vendor.description || '')
  const [website, setWebsite] = useState(vendor.website || '')
  const [phone, setPhone] = useState(vendor.phone || '')
  const [isApproved, setIsApproved] = useState(vendor.is_approved ?? true)
  const [error, setError] = useState<string | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(vendor.logo || null)
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(
    (vendor as Vendor & { background?: string }).background || null
  )
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null)
  const [removeLogo, setRemoveLogo] = useState(false)
  const [removeBackground, setRemoveBackground] = useState(false)

  const logoInputRef = useRef<HTMLInputElement>(null)
  const backgroundInputRef = useRef<HTMLInputElement>(null)

  const updateVendor = useUpdateVendor()

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      setRemoveLogo(false)
      const reader = new FileReader()
      reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setBackgroundFile(file)
      setRemoveBackground(false)
      const reader = new FileReader()
      reader.onload = (ev) => setBackgroundPreview(ev.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLogoFile(null)
    setLogoPreview(null)
    setRemoveLogo(true)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleRemoveBackground = (e: React.MouseEvent) => {
    e.stopPropagation()
    setBackgroundFile(null)
    setBackgroundPreview(null)
    setRemoveBackground(true)
    if (backgroundInputRef.current) backgroundInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (!name.trim()) {
      setError('Vendor name is required')
      return
    }
    
    try {
      await updateVendor.mutateAsync({
        vendorId: vendor.id,
        data: {
          name: name.trim(),
          description: description.trim(),
          website: website.trim(),
          phone: phone.trim(),
          is_approved: isApproved,
          logo: logoFile ? logoFile : removeLogo ? null : undefined,
          background: backgroundFile ? backgroundFile : removeBackground ? null : undefined,
        },
      })
      onClose()
    } catch (err) {
      console.error('Failed to update vendor:', err)
      setError(err instanceof Error ? err.message : 'Failed to update vendor')
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Store className="w-5 h-5 text-blue-600" />
              Edit Vendor
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200 text-sm">
                {error}
              </div>
            )}
            
            {/* Vendor Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                <Store className="w-4 h-4" />
                Vendor Details
              </h3>
              
              {/* Vendor Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter vendor name"
                  className="w-full"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Vendor description"
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              
              {/* Contact Info Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Website URL
                  </label>
                  <Input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Approved toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Approved
                </label>
                <button
                  type="button"
                  onClick={() => setIsApproved(!isApproved)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isApproved ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isApproved ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Branding Section */}
            <div className="space-y-4 border-t border-neutral-200 dark:border-neutral-800 pt-6">
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Vendor Branding
              </h3>
              
              {/* Upload Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Upload Logo */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Logo
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-800"
                    >
                      <div className="flex flex-col items-center">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain mb-2" />
                        ) : (
                          <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                        )}
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          {logoPreview ? 'Change Logo' : 'Upload'}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">PNG, JPG, SVG, WebP up to 2MB</span>
                      </div>
                    </button>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        aria-label="Remove logo"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Upload Background */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Upload Background
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => backgroundInputRef.current?.click()}
                      className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-800"
                    >
                      <div className="flex flex-col items-center">
                        {backgroundPreview ? (
                          <img src={backgroundPreview} alt="Background preview" className="w-16 h-10 object-cover mb-2 rounded" />
                        ) : (
                          <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                        )}
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          {backgroundPreview ? 'Change Background' : 'Upload'}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">JPG, PNG, WebP up to 5MB</span>
                      </div>
                    </button>
                    {backgroundPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveBackground}
                        aria-label="Remove background"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                  />
                </div>
              </div>
              
              {/* Preview Section */}
              {(logoPreview || backgroundPreview) && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Preview
                  </label>
                  <div 
                    className="relative w-full h-40 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700"
                    style={backgroundPreview ? { 
                      backgroundImage: `url(${backgroundPreview})`, 
                      backgroundSize: 'cover', 
                      backgroundPosition: 'center' 
                    } : {}}
                  >
                    {backgroundPreview && (
                      <div className="absolute inset-0 bg-black/20" />
                    )}
                    {logoPreview && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img src={logoPreview} alt="Logo preview" className="max-w-24 max-h-24 object-contain drop-shadow-lg" />
                      </div>
                    )}
                    {!logoPreview && !backgroundPreview && (
                      <div className="absolute inset-0 flex items-center justify-center text-neutral-400">
                        <p className="text-sm">Upload images to see preview</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">This preview shows how your logo will appear on the background</p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={updateVendor.isPending}>
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Bulk Import Modal Component
interface ImportResults {
  vendors_created: number
  vendors_updated: number
  vendor_events_created: number
  items_created: number
  items_updated: number
  vendor_admins_created: number
  vendor_event_admins_created: number
  users_created: number
  warnings: string[]
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.eventpay.com/en/api'

function BulkImportModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const { token } = useAuth()
  const bulkImport = useBulkImportVendors()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragover, setDragover] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [success, setSuccess] = useState(false)
  const [results, setResults] = useState<ImportResults | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragover(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file)
        setError(null)
        setErrors([])
      } else {
        setError('Please select an Excel file (.xlsx or .xls)')
      }
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file)
        setError(null)
        setErrors([])
      } else {
        setError('Please select an Excel file (.xlsx or .xls)')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) return
    setError(null)
    setErrors([])
    setSuccess(false)
    setResults(null)

    try {
      const response = await bulkImport.mutateAsync({ eventId, file: selectedFile })
      
      if (response.success) {
        setSuccess(true)
        setResults(response.results || null)
      } else {
        setError(response.error || 'Import failed')
        setErrors(response.errors || [])
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to import vendors')
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Bulk Import Vendors
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* Success Message */}
          {success && results && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                    Import Completed Successfully
                  </h3>
                  <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                    {results.vendors_created > 0 && (
                      <li>{results.vendors_created} vendor(s) created</li>
                    )}
                    {results.vendors_updated > 0 && (
                      <li>{results.vendors_updated} vendor(s) updated</li>
                    )}
                    {results.vendor_events_created > 0 && (
                      <li>{results.vendor_events_created} location(s) created</li>
                    )}
                    {results.items_created > 0 && (
                      <li>{results.items_created} menu item(s) created</li>
                    )}
                    {results.items_updated > 0 && (
                      <li>{results.items_updated} menu item(s) updated</li>
                    )}
                    {results.vendor_admins_created > 0 && (
                      <li>{results.vendor_admins_created} vendor admin(s) created</li>
                    )}
                    {results.vendor_event_admins_created > 0 && (
                      <li>{results.vendor_event_admins_created} location admin(s) created</li>
                    )}
                    {results.users_created > 0 && (
                      <li>{results.users_created} user(s) created</li>
                    )}
                  </ul>
                  {results.warnings && results.warnings.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Warnings:</p>
                      <ul className="text-xs text-amber-600 dark:text-amber-400 space-y-0.5">
                        {results.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {error && !success && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                  {errors.length > 0 && (
                    <ul className="mt-2 text-sm text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
                      {errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {!success && (
            <>
              {/* Instructions */}
              <div className="mb-6">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                  Upload an Excel file (.xlsx or .xls) to import vendors, menu items, and administrators. The file should have the following tabs:
                </p>
                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-3 text-xs font-mono text-neutral-700 dark:text-neutral-300 space-y-1">
                  <div><strong>VENDORS:</strong> name, description, website, phone, booth</div>
                  <div><strong>MENU ITEMS:</strong> vendor_name, name, price, description, category</div>
                  <div><strong>VENDOR ADMINS:</strong> vendor_name, email</div>
                  <div><strong>VENDOR EVENT ADMINS:</strong> vendor_name, booth, email</div>
                </div>
                <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                  <p className="mb-1">All data is validated before import. If any errors occur, the entire import is rolled back.</p>
                </div>
              </div>

              {/* Download Template Link */}
              <div className="mb-6">
                <a
                  href={`${API_BASE_URL}/event/${eventId}/import-vendors-template/`}
                  download
                  onClick={async (e) => {
                    e.preventDefault()
                    // Download template with auth header
                    try {
                      const response = await fetch(`${API_BASE_URL}/event/${eventId}/import-vendors-template/`, {
                        headers: {
                          'Authorization': `Token ${token}`,
                        },
                      })
                      if (!response.ok) throw new Error('Failed to download template')
                      const blob = await response.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = 'vendor_import_template.xlsx'
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      window.URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Failed to download template:', err)
                      setError('Failed to download template. Please try again.')
                    }
                  }}
                  className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download Excel Template
                </a>
              </div>

              {/* Upload Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* File Drop Zone */}
                <div className="relative">
                  <label
                    htmlFor="excel_file"
                    className="block cursor-pointer"
                    onDragOver={(e) => { e.preventDefault(); setDragover(true) }}
                    onDragLeave={(e) => { e.preventDefault(); setDragover(false) }}
                    onDrop={handleDrop}
                  >
                    <div className={cn(
                      'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                      dragover
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-neutral-300 dark:border-neutral-700'
                    )}>
                      <FileSpreadsheet className="mx-auto h-12 w-12 text-neutral-400" />
                      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <span className="font-medium text-blue-600 dark:text-blue-400">Click to upload</span> or drag and drop
                      </p>
                      <p className="mt-1 text-xs text-neutral-500">Excel files only (.xlsx, .xls)</p>
                      <p className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {selectedFile ? selectedFile.name : 'No file selected'}
                      </p>
                    </div>
                  </label>
                  <input
                    type="file"
                    id="excel_file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!selectedFile || bulkImport.isPending}
                    isLoading={bulkImport.isPending}
                  >
                    {bulkImport.isPending ? 'Importing...' : 'Import Vendors'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// New Location Modal Component
export function NewLocationModal({ eventId, vendors, lockedVendor, onClose }: { eventId: number; vendors: Vendor[]; lockedVendor?: Vendor; onClose: () => void }) {
  const createVendorEvent = useCreateVendorEvent()
  const [booth, setBooth] = useState('')
  const [description, setDescription] = useState('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | ''>(lockedVendor?.id ?? '')
  const [vendorSearch, setVendorSearch] = useState('')
  const [showVendorDropdown, setShowVendorDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowVendorDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(vendorSearch.toLowerCase())
  )

  const selectedVendor = vendors.find(v => v.id === selectedVendorId)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!selectedVendorId) {
      setError('Please select a vendor')
      return
    }
    if (!booth.trim()) {
      setError('Location name is required')
      return
    }

    try {
      await createVendorEvent.mutateAsync({
        data: {
          vendor: selectedVendorId as number,
          event: eventId,
          booth: booth.trim(),
          description: description.trim(),
        },
        eventId,
      })
      onClose()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create location'
      setError(errorMessage)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">New Location</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Add a new vendor location to this event</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Vendor Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Vendor <span className="text-red-500">*</span>
            </label>
            {lockedVendor ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md bg-neutral-50 dark:bg-neutral-800 text-sm text-neutral-900 dark:text-neutral-100">
                <Store className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                {lockedVendor.name}
              </div>
            ) : (
            <div className="relative" ref={dropdownRef}>
              <Input
                value={showVendorDropdown ? vendorSearch : (selectedVendor?.name || '')}
                onChange={(e) => {
                  setVendorSearch(e.target.value)
                  setShowVendorDropdown(true)
                  if (selectedVendorId) setSelectedVendorId('')
                }}
                onFocus={() => setShowVendorDropdown(true)}
                placeholder="Search for a vendor..."
              />
              {showVendorDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredVendors.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-neutral-500">No vendors found</div>
                  ) : (
                    filteredVendors.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setSelectedVendorId(v.id)
                          setVendorSearch('')
                          setShowVendorDropdown(false)
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors',
                          selectedVendorId === v.id && 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        )}
                      >
                        <Store className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                        {v.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            )}
          </div>

          {/* Location Name */}
          <div>
            <label htmlFor="new-location-booth" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Location Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="new-location-booth"
              value={booth}
              onChange={(e) => setBooth(e.target.value)}
              placeholder="e.g. Main Bar, Food Tent A..."
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="new-location-desc" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Description
            </label>
            <textarea
              id="new-location-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="pt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <Button variant="outline" type="button" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={createVendorEvent.isPending} className="w-full sm:w-auto">
              {createVendorEvent.isPending ? 'Creating...' : 'Create Location'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
